// src/auth/AuthActions.js
// Shared auth actions for the whole public site. Any page or the nav can trigger
// sign-in and read busy/error/onboarding state from this one context, so the
// Auth0 popup logic lives in exactly one place.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE_URL } from 'api';
import { chooseProfile, clearActiveProfile, readActiveProfile, writeActiveProfile } from 'utils/activeProfile';
import ProfilePicker from './ProfilePicker';

const AuthActionsContext = createContext(null);

export const useAuthActions = () => {
  const ctx = useContext(AuthActionsContext);
  if (!ctx) throw new Error('useAuthActions must be used within <AuthActionsProvider>');
  return ctx;
};

/**
 * Exchanges a verified Auth0 access token for the customer's meal-plan token via
 * the backend's /auth/token. Never throws — network/parse failures come back as
 * a normal {ok: false} result so callers don't need their own try/catch around it.
 * With several profiles on the email it resolves to the one picked this session,
 * or returns needsProfile so the caller can open the picker.
 * @returns {{ok: true, token: string, fullName: string, profiles: object[]} | {ok: false, needsProfile: true, profiles: object[]} | {ok: false, noAccount: true, message: string} | {ok: false, error: string}}
 */
export const exchangeAuthToken = async (accessToken) => {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/token`, {
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    });

    // Read as text first so a non-JSON error body (e.g. a 401) doesn't throw.
    const text = await res.text();
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch { /* non-JSON response */ }

    if (res.ok && data.profiles?.length) {
      const profiles = data.profiles;
      const chosen = chooseProfile(data);
      if (chosen) return { ok: true, token: chosen.token, fullName: chosen.fullName, profiles };
      return { ok: false, needsProfile: true, profiles };
    }
    if (res.ok && data.token) {
      return { ok: true, token: data.token, fullName: data.fullName || '', profiles: [] };
    }
    if (data.error === 'no_account') {
      return { ok: false, noAccount: true, message: data.message, suggestedName: data.suggestedName || '' };
    }
    if (data.error === 'account_provisioning' || data.error === 'ambiguous_account') {
      return { ok: false, error: data.message };
    }
    console.error('auth/token failed', res.status, text);
    return { ok: false, error: `Couldn't load your account (server returned ${res.status}). Please try again.` };
  } catch (e) {
    console.error('auth/token fetch error', e);
    return { ok: false, error: `Couldn't reach the server (${e.message || 'network error'}). Make sure the backend is running.` };
  }
};

/** Creates the starter profile for a signed-in customer with no Client row. */
export const provisionAccount = async (accessToken, firstName) => {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/provision`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName }),
    });
    const text = await res.text();
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch { /* non-JSON response */ }
    if (res.ok && data.success) return { ok: true };
    if (data.error === 'already_exists') return { ok: false, alreadyExists: true };
    return { ok: false, error: data.message || `Couldn't set up your account (server returned ${res.status}).` };
  } catch (e) {
    return { ok: false, error: `Couldn't reach the server (${e.message || 'network error'}).` };
  }
};

/** Shared error/onboarding banners for any layout using AuthActionsProvider. */
export function AuthStatusBanners() {
  const { error, onboarding } = useAuthActions();

  return (
    <>
      {error && (
        <div style={{
          background: '#fdecec', borderBottom: '1px solid #f5c2c2', color: '#cc3333',
          padding: '12px 28px', fontSize: '14px', textAlign: 'center',
        }}>
          {error}
        </div>
      )}
      {onboarding && (
        <div style={{
          background: '#fff8e6', borderBottom: '1px solid #f0dca6', color: '#7a5c00',
          padding: '16px 28px', fontSize: '14px', textAlign: 'center', lineHeight: 1.6,
        }}>
          {onboarding.status === 'provisioning' && 'Setting up your Square Fare account…'}
          {onboarding.status === 'ready' && (onboarding.name ? (
            <>
              Welcome, <strong>{onboarding.name}</strong> — we took that name from your email
              address. If it isn&apos;t right, please change it in the <strong>Account's Profile</strong>{' '}
              panel: we need it to match your quiz results and orders.
            </>
          ) : (
            <>
              Welcome — we couldn&apos;t read a name from your email address. Please add yours in
              the <strong>Account's Profile</strong> panel: we need it to match your quiz results and orders.
            </>
          ))}
          {onboarding.status === 'slow' && (
            <>
              Your account is still being set up. Give it a moment, then{' '}
              <button type="button" onClick={() => window.location.reload()} style={linkButtonStyle}>
                refresh
              </button>.
            </>
          )}
        </div>
      )}
    </>
  );
}

const linkButtonStyle = {
  background: 'none', border: 'none', padding: 0, font: 'inherit',
  color: '#1a1a1a', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer',
};

// Per-tab, survives the round trip: marks the load that finished a sign-in we started,
// so resolveAndGo doesn't fire on every load of an already-signed-in customer.
const LOGIN_PENDING = 'sf_login_pending';

// Unique ID is stamped by an automation, so poll ~20s before giving up.
const PROVISION_POLL_ATTEMPTS = 8;
const PROVISION_POLL_INTERVAL_MS = 2500;

// Auth0 throws for "nobody is signed in" — no session, an abandoned flow — which
// isn't a fault worth a banner.
const isUnfinishedSignIn = (e) => {
  const code = String(e?.error || '');
  return /^(cancelled|timeout|login_required|consent_required|missing_refresh_token)$/i.test(code);
};

export function AuthActionsProvider({ children }) {
  const { loginWithRedirect, getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const [error, setError] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [busy, setBusy] = useState(false);
  // Login no longer hard-redirects to /meal-plan. It resolves the account in
  // place and flips this flag — the header watches it and opens the account
  // panel, so the customer stays where they are and navigates via the site.
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const redirecting = useRef(false);
  // True from the Auth0 redirect back until the login lands on an account, so the
  // header keeps saying "Opening…" through the profile picker instead of "Account".
  const [signingIn, setSigningIn] = useState(() => {
    try { return sessionStorage.getItem(LOGIN_PENDING) === '1'; } catch { return false; }
  });
  // Every profile on the signed-in email; the picker is open while `picker` is set.
  const [profiles, setProfiles] = useState([]);
  const [picker, setPicker] = useState(null);
  const [activeToken, setActiveToken] = useState(() => readActiveProfile()?.token || null);

  const getToken = () => getAccessTokenSilently({
    authorizationParams: {
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      scope: 'openid profile email offline_access',
    },
  });

  // Creates the profile, then waits for the Unique ID /auth/token needs.
  const provision = async (suggestedName) => {
    const name = String(suggestedName || '').trim();
    setOnboarding({ name, status: 'provisioning' });
    setError(null);
    try {
      const created = await provisionAccount(await getToken(), name);
      // 409 means a row already exists (a race, or a retry) — poll rather than fail.
      if (!created.ok && !created.alreadyExists) {
        setOnboarding(null);
        setError(created.error);
        return;
      }
      for (let attempt = 0; attempt < PROVISION_POLL_ATTEMPTS; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, PROVISION_POLL_INTERVAL_MS));
        const result = await exchangeAuthToken(await getToken());
        if (result.ok) {
          setOnboarding({ name, status: 'ready' });
          setJustLoggedIn(true);
          return;
        }
      }
      setOnboarding({ name, status: 'slow' });
    } catch (e) {
      console.error('provision error', e);
      setOnboarding(null);
      setError("We couldn't finish setting up your account. Please try again.");
    }
  };

  // After sign-in, exchange the Auth0 session for the customer's token and
  // confirm the account exists. Runs at most once per attempt.
  const resolveAndGo = async () => {
    if (redirecting.current) return;
    redirecting.current = true;
    try {
      const result = await exchangeAuthToken(await getToken());
      if (result.profiles) setProfiles(result.profiles);

      if (result.ok) {
        // Stay on the current page; signal the header to open the account panel.
        redirecting.current = false;
        setSigningIn(false);
        setJustLoggedIn(true);
      } else if (result.needsProfile) {
        // signingIn stays on until a profile is picked.
        redirecting.current = false;
        setPicker({ dismissible: false });
      } else if (result.noAccount) {
        redirecting.current = false;
        provision(result.suggestedName).finally(() => setSigningIn(false));
      } else {
        redirecting.current = false;
        setSigningIn(false);
        setError(result.error);
      }
    } catch (e) {
      redirecting.current = false;
      setSigningIn(false);
      if (isUnfinishedSignIn(e)) return;
      console.error('login resolve error', e);
      setError("We couldn't finish signing you in. Please try again.");
    }
  };

  // Known on every signed-in load, so the Switch profile button knows whether to show.
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await exchangeAuthToken(await getToken());
        if (!cancelled && result.profiles) setProfiles(result.profiles);
      } catch { /* the login flow reports its own errors */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  const pickProfile = (profile) => {
    const switching = activeToken && activeToken !== profile.token;
    writeActiveProfile(profile);
    setActiveToken(profile.token);
    setPicker(null);
    setSigningIn(false);
    // Pages hold the previous profile's data; reload the meal plan on its new token.
    if (window.location.pathname === '/meal-plan') {
      window.location.href = `/meal-plan?customer=${encodeURIComponent(profile.token)}`;
      return;
    }
    if (switching) {
      window.location.reload();
      return;
    }
    setJustLoggedIn(true);
  };

  const openProfilePicker = useCallback(() => setPicker({ dismissible: true }), []);

  // A redirect tears the page down, so finish the exchange on the load that returns.
  // Remounting PublicLayout → FlowLayout (returnTo /checkout) is safe: auth0-react runs
  // onRedirectCallback before dispatching INITIALISED, so only the final layout sees it.
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      setSigningIn(false);
      return;
    }
    if (sessionStorage.getItem(LOGIN_PENDING) !== '1') return;
    sessionStorage.removeItem(LOGIN_PENDING);
    resolveAndGo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  // Redirect, not popup: browsers that turn window.open into a tab move focus to the
  // neighbouring tab when Auth0 closes it. Passwordless has no sign-up screen, so one
  // action covers both and `screen_hint` would be a no-op.
  const handleLogin = async () => {
    setError(null);
    setOnboarding(null);
    setBusy(true);
    clearActiveProfile();
    try {
      sessionStorage.setItem(LOGIN_PENDING, '1');
      // Audience so the cached token works against our backend; busy stays set — the
      // navigation is what ends it.
      await loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search },
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email offline_access',
        },
      });
    } catch (e) {
      sessionStorage.removeItem(LOGIN_PENDING);
      setBusy(false);
      if (isUnfinishedSignIn(e)) return;
      console.error('login redirect error', e);
      setError('Sign-in failed. Please try again.');
    }
  };

  const value = {
    busy: busy || signingIn,
    error,
    onboarding,
    justLoggedIn,
    clearJustLoggedIn: () => setJustLoggedIn(false),
    // Called once the customer saves a name from the account panel — the banner
    // only exists to ask for that.
    dismissOnboarding: () => setOnboarding(null),
    handleLogin,
    profiles,
    activeToken,
    openProfilePicker,
    signOutCleanup: clearActiveProfile,
  };

  return (
    <AuthActionsContext.Provider value={value}>
      {children}
      {picker && profiles.length > 0 && (
        <ProfilePicker
          profiles={profiles}
          activeToken={activeToken}
          onPick={pickProfile}
          onClose={picker.dismissible ? () => setPicker(null) : null}
        />
      )}
    </AuthActionsContext.Provider>
  );
}

/** Reopens the profile picker; renders nothing when the email has one profile. */
export function SwitchProfileButton({ style, onClick }) {
  const { profiles, openProfilePicker } = useAuthActions();
  if (profiles.length < 2) return null;
  return (
    <button
      type="button"
      onClick={() => { onClick?.(); openProfilePicker(); }}
      style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        font: 'inherit', fontSize: '13px', textDecoration: 'underline', color: 'inherit',
        ...style,
      }}
    >
      Switch profile
    </button>
  );
}
